define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-construct",
	"dojo/dom-class",
	"dojo/dom-geometry",
	"dojo/dom-style",
	"dojo/topic",
	"dijit/place",
	"dijit/Tooltip",
	"csi/html",
	"csi/state"
], function(declare, lang, domConstruct, domClass, domGeometry, domStyle, topic, place, Tooltip, html, state) {

	var _lastRootCss;

	// Extend Dojo master tooltip so master tooltip is sticky and supports events
	var _masterTT;

	var TOOLTIP_POSITIONS = ["after-centered", "before-centered", "above-centered", "below-centered"];

	var _MasterTooltip = declare(Tooltip._MasterTooltip, {
		// Tooltip: tooltip currently be displayed
		ttCurrent: null,

		// Boolean: force tooltip to remain open even if mouse leaves node
		bPin: false,

		/**
		 * Apply CSS to tooltip, making sure to remove the previous CSS
		 * @param {string} [css] 
		 */
		applyCss: function(css) {
			// NOTE: apply to connectorNode to have access to both children; applying to DOMNode does not hold (class must get reset somewhere)
			if (css !== _lastRootCss) {
				if (_lastRootCss) {
					domClass.remove(this.domNode, _lastRootCss);
				}

				_lastRootCss = css;

				if (_lastRootCss) {
					domClass.add(this.domNode, _lastRootCss);
				}
			}
		},

		postCreate: function() {
			this.inherited(arguments);
			this.connect(this.domNode, "onmouseover", this._onMouseOverSticky);
			this.connect(this.domNode, "onmouseout", this._onMouseOutSticky);
			this.connect(this.domNode, "onclick", this._onClickTooltip);
			this.connect(this.domNode, "wheel", (e) => e.preventDefault());
		},

		pin: function(/*Boolean*/bPin) {
			// summary: pinning a tooltip will force it to remain open
			var playing = (this.fadeOut.status() == "playing");

			if (bPin) {
				if (playing) {
					this.restore();
				}
			} else if (!playing) {
				this.fadeOut.play();
				this.isShowingNow = false;
			}

			this.bPin = bPin;
		},

		restore: function() {
			// summary: stop fade-out and fade back in
			this.fadeOut.stop();
			this.fadeIn.play();
			this.isShowingNow = true;

			if (this.ttCurrent) {
				this.ttCurrent.onFadeIn();
			}
		},

		_onHide: function() {
			// summary: when tooltip is finally hidden, reset tooltip
			if (this.ttCurrent && this.ttCurrent.onFadeOut) {
				this.ttCurrent.onFadeOut();
			}

			topic.publish("/csi/tooltip/hide", this.ttCurrent);

			this.inherited(arguments);
		},

		_onMouseOverSticky: function(/*Event*/evt) {
			if (this.fadeOut.status() == "playing") {
				this.restore();
			}
		},

		_onMouseOutSticky: function(/*Event*/evt) {
			if (!this.bPin) {
				this.fadeOut.play();
				this.isShowingNow = false;
			}
		},

		_onClickTooltip: function(/*Event*/evt) {
			if (this.ttCurrent && this.ttCurrent.onClick) {
				this.ttCurrent.onClick(evt);
			}
		},

		orient: function(/*DomNode*/ node, /*String*/ aroundCorner, /*String*/ tooltipCorner, /*Object*/ spaceAvailable, /*Object*/ aroundNodeCoords) {
			// summary:
			//		Private function to set CSS for tooltip node based on which position it's in.
			//		This is called by the dijit popup code.   It will also reduce the tooltip's
			//		width to whatever width is available
			// tags:
			//		protected

			this.connectorNode.style.top = ""; //reset to default

			var heightAvailable = spaceAvailable.h,
				widthAvailable = spaceAvailable.w;

			node.className = "dijitTooltip " +
				{
					"MR-ML": "dijitTooltipRight",
					"ML-MR": "dijitTooltipLeft",
					"TM-BM": "dijitTooltipAbove",
					"BM-TM": "dijitTooltipBelow",
					"BL-TL": "dijitTooltipBelow dijitTooltipABLeft",
					"TL-BL": "dijitTooltipAbove dijitTooltipABLeft",
					"BR-TR": "dijitTooltipBelow dijitTooltipABRight",
					"TR-BR": "dijitTooltipAbove dijitTooltipABRight",
					"BR-BL": "dijitTooltipRight",
					"BL-BR": "dijitTooltipLeft"
				}[aroundCorner + "-" + tooltipCorner];

			// reset width; it may have been set by orient() on a previous tooltip show()
			this.domNode.style.width = "auto";

			// Reduce tooltip's width to the amount of width available, so that it doesn't overflow screen.
			// Note that sometimes widthAvailable is negative, but we guard against setting style.width to a
			// negative number since that causes an exception on IE.
			var size = domGeometry.position(this.domNode);

			// CENTRIC BEGIN
			//Use the width of domNode if the targetWidth is more smaller rather than just use the small one
			//In some cases(For example, resize the window a little bit smaller)
			//The Math.min((Math.max(widthAvailable,1)), size.w) returns a smaller value than the require width of domNode;
			var targetWidth = Math.min((Math.max(widthAvailable, 1)), size.w);
			var width = targetWidth < size.w ? size.w : targetWidth;
			// CENTRIC END

			domGeometry.setMarginBox(this.domNode, {w: width});

			// Reposition the tooltip connector.
			if (tooltipCorner.charAt(0) == 'B' && aroundCorner.charAt(0) == 'B') {
				var bb = domGeometry.position(node);
				var tooltipConnectorHeight = this.connectorNode.offsetHeight;
				if (bb.h > heightAvailable) {
					// The tooltip starts at the top of the page and will extend past the aroundNode
					var aroundNodePlacement = heightAvailable - ((aroundNodeCoords.h + tooltipConnectorHeight) >> 1);
					this.connectorNode.style.top = aroundNodePlacement + "px";
					this.connectorNode.style.bottom = "";
				} else {
					// Align center of connector with center of aroundNode, except don't let bottom
					// of connector extend below bottom of tooltip content, or top of connector
					// extend past top of tooltip content
					this.connectorNode.style.bottom = Math.min(
						Math.max(aroundNodeCoords.h / 2 - tooltipConnectorHeight / 2, 0),
						bb.h - tooltipConnectorHeight) + "px";
					this.connectorNode.style.top = "";
				}
			} else {
				// reset the tooltip back to the defaults
				this.connectorNode.style.top = "";
				this.connectorNode.style.bottom = "";
			}

			return Math.max(0, size.w - widthAvailable);
		},

		show: function(/*String|Tooltip*/tooltip, /*DOMNode*/aroundNode, /*String[]?*/position, /*Boolean*/rtl, /*String*/textDir) {
			// CENTRIC: DOJO INTERNAL - change function signature to accept Tooltip rather than just innerHTML;
			// also check that placement of tooltip has height (in other words, is in DOM)
			var bString = (typeof tooltip === "string");

			if (state.get("dndInProgress")) {
				return;
			}

			if (!aroundNode || !aroundNode.offsetHeight) {
				return;
			}

			// reset width; it may have been set by orient() on a previous tooltip show()
			this.domNode.style.width = "auto";

			if (this.fadeOut.status() == "playing") {
				// previous tooltip is being hidden; wait until the hide completes then show new one
				this._onDeck = arguments;
				return;
			}

			// clear previous CSS
			this.applyCss();

			// CENTRIC - change how tooltip is populated
			if (bString) {
				this.containerNode.innerHTML = tooltip;
			} else if (tooltip.childNodes) {
				domConstruct.place(tooltip, this.containerNode, "only");
			} else if (tooltip.domNode) {
				html.destroyChildren(this.containerNode);
				this.ttCurrent = tooltip;
				this.ttCurrent.onFadeIn();
				this.fadeIn.duration = this.fadeOut.duration = tooltip.fadeDelay;

				while (tooltip.domNode.firstChild) {
					this.containerNode.appendChild(tooltip.domNode.firstChild);
				}
			} else {
				return;
			}

			var pos = place.around(this.domNode, aroundNode, position && position.length ? position : TOOLTIP_POSITIONS, !rtl, lang.hitch(this, "orient"));

			// Position the tooltip connector for middle alignment.
			// This could not have been done in orient() since the tooltip wasn't positioned at that time.
			var aroundNodeCoords = pos.aroundNodePos;
			if (pos.corner.charAt(0) == 'M' && pos.aroundCorner.charAt(0) == 'M') {
				this.connectorNode.style.top = aroundNodeCoords.y + ((aroundNodeCoords.h - this.connectorNode.offsetHeight) >> 1) - pos.y + "px";
				this.connectorNode.style.left = "";
			} else if (pos.corner.charAt(1) == 'M' && pos.aroundCorner.charAt(1) == 'M') {
				this.connectorNode.style.left = aroundNodeCoords.x + ((aroundNodeCoords.w - this.connectorNode.offsetWidth) >> 1) - pos.x + "px";
			}

			// show it
			domStyle.set(this.domNode, "opacity", 0);
			this.fadeIn.play();
			this.isShowingNow = true;
			this.aroundNode = aroundNode;

			// CENTRIC: reset pin and notify others that tooltip is shown
			this.bPin = false;
			topic.publish("/csi/tooltip/show", tooltip, aroundNode);
		}
	});

	function _getDOMNode(/*Object*/obj) {
		return obj.domNode || obj;
	}

	function _getMaster() {
		// summary: create master tooltip if it does not exist
		return _masterTT = _masterTT || new _MasterTooltip();
	}

	/**
	 * Enable/disable MUI style tooltips
	 * @param {boolean} useMuiTooltipStyling
	 */
	Tooltip.applyMuiStyling = (useMuiTooltipStyling) => {
		document.body.classList.toggle("csi-dojo-to-mui-tooltips", useMuiTooltipStyling);
	};

	/**
	 * Change styling of master tooltip
	 * @param {string} css 
	 */
	Tooltip.setMasterCss = function(css) {
		_getMaster().applyCss(css);
	};

	Tooltip.pin = function(/*Boolean*/bPin) {
		// summary: if bPin is true, then fade in tooltip and don't fade out
		return _getMaster().pin(bPin);
	};

	/**
	 * Return true if tooltip is open now
	 * @return {boolean}
	 */
	Tooltip.isOpen = () => {
		return _getMaster().isShowingNow;
	};

	Tooltip.show = dijit.showTooltip = function(/*String|Tooltip*/tooltip, /*DOMNode|Widget*/aroundNode, /*String[]?*/position, /*Boolean*/rtl, /*String*/textDir) {
		// summary: show the tooltip
		return _getMaster().show(tooltip, _getDOMNode(aroundNode), position, rtl, textDir);
	};

	Tooltip.hide = dijit.hideTooltip = function(/*DOMNode|Widget?*/aroundNode) {
		// summary: hide the tooltip
		if (!_masterTT) return;

		if (aroundNode) {
			aroundNode = _getDOMNode(aroundNode);
		} else {
			// hide whatever is showing immediately if no element passed
			aroundNode = _masterTT.aroundNode;
			html.setPixels(_masterTT.domNode, html.LEFT, -2000);
		}

		return _masterTT.hide(aroundNode);
	};

	/**
	 * Build the tooltip label node for custom field name and value
	 * @param {string} customFieldLabel
	 * @param {string} customFieldTooltip
	 */
	Tooltip.formatCustomField = function(customFieldLabel, customFieldTooltip) {
		let tooltipLabel = html.create("div", {"class": "csi-tooltip-custom-field-title-content", innerText: customFieldLabel});

		if (customFieldTooltip !== "") {
			const nodeCustomTooltip = html.create("div", {"class": "csi-tooltip-custom-field-custom-tooltip-content", innerText: customFieldTooltip});
			const tmpTooltipLabel = html.create("div");
			tmpTooltipLabel.appendChild(tooltipLabel);
			tmpTooltipLabel.appendChild(nodeCustomTooltip);
			tooltipLabel = tmpTooltipLabel;
		}

		return tooltipLabel;
	};

	// React style tooltip time delay
	Tooltip.TOOLTIP_REACT_SHOW_DELAY = 2000;

	// Dojo style tooltip time delay
	Tooltip.TOOLTIP_SHOW_DELAY = 500;

	return Tooltip;
});